import React, { useState, useEffect, useMemo } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useActiveLeague } from '../hooks/useActiveLeague';
import { Header } from "../components/common/Header.jsx";
import { Footer } from "../components/common/Footer.jsx";
import { Sidebar } from "../components/common/Sidebar.jsx";
import { Chat } from "../components/common/Chat.jsx";
import { isPublicRoute } from "../components/utils.js";
import { useAuth } from "../hooks/useAuth.js";
import { useGetUserLeagues } from "../api/leaguesApi";
import { useGetTeams } from "../api/teamsApi";
import { useQuickplayState } from "../api/quickplayApi";
import { markQuickplayFunnelComplete, readQuickplayFunnelComplete } from "../utils/quickplayFunnel";
import { Spinner } from "../components/common/Spinner";
import { useQueryClient } from "@tanstack/react-query";
import { CurrentTeamIdProvider } from "../context/CurrentTeamIdContext";
import { TutorialProvider } from "../context/TutorialContext";
import { useAutoHideNav } from "../hooks/useAutoHideNav";
import TransferRevealProvider from "../components/cards/TransferRevealProvider";

function Layout() {
    const message = {
        // text : "THE SEASON CANNOT START UNTIL ALL  OF THE TEAMS IN YOUR LEAGUE HAVE AT LEAST 22 PLAYERS IN THEIR SQUADS.",
        // link: '/onboarding'
    }

    // Get reactive state and logout function from AuthContext via useAuth hook
    const { session, user, loading: authLoading, logout } = useAuth(); 
    const navigate = useNavigate();
    const location = useLocation();
    const currentPathIsPublic = isPublicRoute(location.pathname);
    const queryClient = useQueryClient();

    // Determine auth status directly from session state
    const isNowAuthenticated = !!session;

    const [showPersonal, setShowPersonal] = useState(false);
    const [showChat, setShowChat] = useState(false);
    // Get active league from the global provider
    const { activeLeague } = useActiveLeague();
    
    // Fetch user leagues ONLY if user is authenticated (session exists)
    const { data: userLeagues, isLoading: isLoadingLeagues } = useGetUserLeagues(
        isNowAuthenticated // Enable fetch based on reactive auth status
    );

    // Utility to safely compare mongo ObjectIds / strings / objects
    const equalIds = (a, b) => {
        if (!a || !b) return false;
        const get = (v) => typeof v === 'string' ? v : (v._id ?? v.$oid ?? v.id ?? v.toString?.()) ;
        return get(a) === get(b);
    };

    // Slug now comes directly from ActiveLeagueProvider derived from URL param
    const leagueSlug = activeLeague?.slug ?? null;

    // STEP 1b ───────────────────────────────────────────────────────────────
    // Extract a team id from team-centric routes ("/team-pitch/:id" or
    // "/team-list/:id") so that we can later infer the league when no slug
    // is present in the URL.
    const teamRouteMatch = location.pathname.match(/^\/team-(?:pitch|list)\/([^/]+)/);
    const teamIdFromPath = teamRouteMatch ? teamRouteMatch[1] : null;

    // Pre-compute leagueId to be used when fetching a user's teams.  The slug
    // (known immediately from the URL) is passed as well so the query can start
    // right away instead of waiting for the leagues list to resolve the _id —
    // this is what used to delay the "My team" menu entry by several seconds.
    const leagueIdForTeams = activeLeague?._id ?? null;

    const { data: myTeamsResponse, isLoading: isLoadingTeams } = useGetTeams(
        isNowAuthenticated && !!(activeLeague?.slug || activeLeague?._id),
        leagueIdForTeams,
        activeLeague?.slug ?? null
    );
    // myTeamsResponse can be undefined, a single team object, or an array of teams
    const myTeams = useMemo(() => (
        Array.isArray(myTeamsResponse)
            ? myTeamsResponse
            : myTeamsResponse
                ? [myTeamsResponse]
                : []
    ), [myTeamsResponse]);
    
    // If you still need hasLeagues elsewhere:
    const hasLeagues = Array.isArray(userLeagues) && userLeagues.length > 0;
    const hasMultipleLeagues = Array.isArray(userLeagues) && userLeagues.length > 1; // In case we need it later



    // NOTE: no manual refetch needed when the league resolves — the teams query
    // is keyed by slug, so react-query refetches automatically when it changes.

    // Function to handle league navigation
    const handleLeagueNavigation = (e) => {
        e.preventDefault();
        
        if (isLoadingLeagues) {
            navigate('/onboarding');
            return;
        }
        
        if (hasLeagues) {
            navigate(`/league/${leagueSlug}/table`);
        } else {
            navigate('/onboarding');
        }
    };

    const PageAlert = (obj) => {
        if (!obj || !obj.message || !obj.message.text) {
            return null;
        }

        return (
            <div className="page-alert">
                {obj.message.text}
                {obj.message.link && (
                    <Link
                        className="alert-link icon-arrow-right"
                        to={obj.message.link}
                    >
                    </Link>
                )}
            </div>
        )
    }

    // Invalidate league/team related queries when leagueSlug changes
    useEffect(() => {
        if (leagueSlug) {
            queryClient.invalidateQueries({ predicate: (q) => {
                const key = q.queryKey?.[0];
                return [
                    "slug-league",
                    "league-fixtures",
                    "teams",
                    "team",
                    "team-players",
                    "team-teamsheet"
                ].includes(key);
            }});
        }
    }, [leagueSlug, queryClient]);

    // Determine if header should be shown based on reactive auth status
    const showHeader = isNowAuthenticated && !currentPathIsPublic && location.pathname !== '/';

    // On mobile the fixed bars hide while scrolling down and return on the
    // first upward scroll, freeing vertical space (CSS is mobile-scoped).
    const isNavAutoHidden = useAutoHideNav(showHeader);

    const isLeagueSelectPage = location.pathname === '/switch-league';

    // Treat both modern slug-first paths ("/<slug>/…") **and** legacy prefixed paths
    // ("/league/<slug>/…") as being "inside" the active league. This ensures
    // league-scoped UI elements such as the Create-Team CTA banner appear no
    // matter which URL variant the user is on.
    const isInsideActiveLeague = leagueSlug && (
        location.pathname.startsWith(`/${leagueSlug}`) ||
        location.pathname.startsWith(`/league/${leagueSlug}`)
    );

    // Compute current team id for active league using user's teams list
    const currentTeamId = useMemo(() => {
        if (!activeLeague || !Array.isArray(myTeams) || !user) return null;
        const leagueId = activeLeague._id ?? activeLeague.id;
        const leagueTeamIds = (activeLeague.teams || []).map(id => id?._id ?? id);
        // Prefer strict match (team listed in league AND has leagueId in its own leagues)
        let myTeam = myTeams.find(t => {
            const idMatch = leagueTeamIds.some(lid => equalIds(lid, t._id));
            const leagueFieldMatch = (
                (t.leagues || []).some(lid => equalIds(lid, leagueId)) ||
                (t.league && equalIds(t.league, leagueId))
            );
            console.log('%c[TEAM_MATCH_CHECK]','color:purple', {
                teamId: t._id,
                teamName: t.name,
                idMatch,
                leagueFieldMatch,
                consideredStrict: idMatch && leagueFieldMatch
            });
            return idMatch && leagueFieldMatch;
        });
        // Fallback 1: team.leagues contains leagueId (but league.teams not populated)
        if (!myTeam) {
            myTeam = myTeams.find(t => {
                const leagueFieldMatch = (
                    (t.leagues || []).some(lid => equalIds(lid, leagueId)) ||
                    (t.league && equalIds(t.league, leagueId))
                );
                if (leagueFieldMatch) {
                    console.log('%c[FALLBACK_MATCH] leagueFieldMatch only','color:green', {teamId: t._id, teamName: t.name});
                }
                return leagueFieldMatch;
            });
        }
        // Fallback 2: league.teams contains teamId (but team.leagues missing leagueId)
        if (!myTeam) {
            myTeam = myTeams.find(t => {
                const idMatch = leagueTeamIds.some(lid => equalIds(lid, t._id));
                if (idMatch) {
                    console.log('%c[FALLBACK_MATCH] idMatch only','color:green', {teamId: t._id, teamName: t.name});
                }
                return idMatch;
            });
        }
        // Fallback 3: the teams query is scoped to the active league (by slug or
        // id), so every returned team belongs to it. Covers the early phase where
        // activeLeague is still a {slug} stub without _id/teams populated.
        if (!myTeam && myTeams.length > 0 && (activeLeague.slug || leagueId)) {
            myTeam = myTeams[0];
        }
        return myTeam ? myTeam._id || null : null;
    }, [activeLeague, myTeams, user]);
    const currentTeamName = currentTeamId
        ? myTeams.find(team => equalIds(team?._id, currentTeamId))?.name || null
        : null;

    // QUICK PLAY FUNNEL
    // Quick Play managers follow a short guided loop (team, match, transfers).
    // While that loop is open the menu links are replaced by the step bar, and
    // the loop closes for good once the manager reaches the transfers page.
    const isQuickplayLeague = activeLeague?.gameMode === 'quickplay';
    const quickplayLeagueId = isQuickplayLeague
        ? (activeLeague?._id ?? activeLeague?.id ?? null)
        : null;

    const { data: quickplayState } = useQuickplayState(isNowAuthenticated && isQuickplayLeague);
    const quickplayMatchId = quickplayState?.activeMatch?._id
        || quickplayState?.lastMatch?._id
        || null;
    const isQuickplayMatchCompleted = quickplayState?.lastMatch?.status === 'completed';
    const isQuickplayMatchStarted = isQuickplayMatchCompleted || [
        'waitingForReady',
        'waitingForEngine',
        'inProgress'
    ].includes(quickplayState?.activeMatch?.status);

    const isQuickplayTransfersRoute = !!leagueSlug && isQuickplayLeague && (
        location.pathname.startsWith(`/${leagueSlug}/transfers`) ||
        location.pathname.startsWith(`/league/${leagueSlug}/transfers`)
    );

    const [funnelCompleteLeagueId, setFunnelCompleteLeagueId] = useState(null);
    const isStoredFunnelComplete = useMemo(
        () => readQuickplayFunnelComplete(quickplayLeagueId),
        [quickplayLeagueId]
    );
    const isQuickplayFunnelComplete = !!quickplayLeagueId && (
        isStoredFunnelComplete || funnelCompleteLeagueId === quickplayLeagueId
    );

    // Reaching transfers closes the guided loop for every Quick Play manager.
    useEffect(() => {
        if (!quickplayLeagueId || !isQuickplayTransfersRoute) {
            return;
        }
        markQuickplayFunnelComplete(quickplayLeagueId);
        setFunnelCompleteLeagueId(quickplayLeagueId);
    }, [quickplayLeagueId, isQuickplayTransfersRoute]);

    const showQuickplayFunnel = isQuickplayLeague
        && !!leagueSlug
        && !isQuickplayFunnelComplete;

    // Check loading state from AuthContext
    if (authLoading) {
        return <Spinner />; // Or a dedicated loading screen
    }

    return (
        <TutorialProvider>
            <div className={`layout ${showPersonal ? 'show-personal' : ''} ${showChat ? 'show-chat' : ''} ${showQuickplayFunnel ? 'quickplay-funnel-active' : ''} ${isNavAutoHidden ? 'nav-hidden' : ''}`}>
                <PageAlert message={message} />
                {showHeader && (
                     <Header 
                         user={user} // Pass reactive user from context
                         logout={logout} // Pass logout from context
                         showPersonal={showPersonal} 
                         setShowPersonal={setShowPersonal} 
                         showChat={showChat} 
                         setShowChat={setShowChat}
                         handleLeagueNavigation={handleLeagueNavigation}
                         isLoadingLeagues={isLoadingLeagues}
                         hasLeagues={hasLeagues}
                         leagueSlug={leagueSlug}
                         currentTeamId={currentTeamId}
                         isLeagueSelectPage={isLeagueSelectPage}
                         hideMainNav={showQuickplayFunnel}
                         quickplayMatchId={quickplayMatchId}
                         isQuickplayMatchStarted={isQuickplayMatchStarted}
                         isQuickplayMatchCompleted={isQuickplayMatchCompleted}
                     />
                 )}
                 <div className="main-content">
                    {/* CTA banner when user has no team in the league *whose page the user is viewing* */}
                    {isNowAuthenticated && isInsideActiveLeague && !!activeLeague?._id && !currentTeamId && !isLoadingTeams && (
                        <div className="cta-banner" style={{textAlign:'center', background:'#191d37', color:'#ffffff', padding:'10px', marginBottom:'10px'}}>
                            <p>You haven't registered a team in this league yet</p>
                            
                            <Link to={`/create-team?leagueId=${activeLeague._id ?? activeLeague.id}`} className="cta-btn">
                                 Create Team <span className="ico icon-arrow-right"></span>
                             </Link>
                        </div>
                    )}
                     {/* {showHeader && <Sidebar />} */}{/* Temporarily remove Sidebar rendering */}
                     <main className="main">
                        {/* Let child routes handle redirection/access control based on useAuth() */}
                        <CurrentTeamIdProvider value={currentTeamId}>
                            <TransferRevealProvider teamName={currentTeamName}>
                                <Outlet />
                            </TransferRevealProvider>
                        </CurrentTeamIdProvider>
                     </main>
                </div>
                <Chat showChat={showChat} setShowChat={setShowChat} />
                {/* Footer might need conditional rendering based on auth/route */}
                 {/* {!currentPathIsPublic && <Footer />} */}{/* Temporarily remove Footer rendering */} 
            </div>
        </TutorialProvider>
    );
}

export { Layout };
